import maplibregl from "maplibre-gl";
import { GameClient, runDisplay, finalMinutes, type ArchivedRound } from "../net";
import { roster, type Roster } from "../api";
import { STATIONS, STATION_INDEX } from "../stations";
import { clippedZoneFC, offLimitsFC, buildMask, isBlocked } from "../geo";
import { fmtMin, esc } from "./util";

// Floating "Game info" button + modal shown on every player/gm screen:
// current hider, team rosters, live scores, claim-hider, and past-round review.
export function mountInfo(client: GameClient) {
  document.getElementById("info-fab")?.remove();
  document.getElementById("info-modal")?.remove();

  const fab = document.createElement("button");
  fab.id = "info-fab";
  fab.textContent = "ⓘ Game info";
  document.body.appendChild(fab);

  let names: Roster | null = null;
  roster(client.gameId).then((r) => (names = r)).catch(() => {});
  const teamName = (id: string | null) => (id ? names?.teams.find((t) => t.id === id)?.name ?? id.slice(0, 6) : "—");

  fab.addEventListener("click", async () => {
    document.querySelector("#pod-sched-ov")?.remove(); // don't stack with the schedule overlay
    if (!names) { try { names = await roster(client.gameId); } catch { /* ignore */ } }
    openInfo();
  });

  client.onClaimDenied = (reason) => alert(reason);

  function openInfo() {
    if (client.mode === "podrotation") return openPodInfo();
    const group = client.mode === "group";
    const teams = names?.teams ?? [];
    const players = names?.players ?? [];
    const pname = (id: string) => players.find((p) => p.id === id)?.displayName ?? id.slice(0, 6);
    const curHider = group ? (client.hiderSet.length ? client.hiderSet.map(pname).join(", ") : "none yet") : teamName(client.hiderTeamId);
    const canClaim = !client.identity.gm && client.role === "seeker";
    const hideRunKey = group ? "__hiders__" : client.hiderTeamId;

    const pct = client.config.bonusPct;
    const scoreRows = group
      ? (() => { const sc = client.scoresAll["__hiders__"]; const d = runDisplay(client.runsAll["__hiders__"] ?? null, client.config.headStartSec); const fin = finalMinutes(d.runMs, sc, pct, false); return `<tr><td>${client.hiderSet.length ? client.hiderSet.map(pname).join(", ") : "(no hiders yet)"}</td><td class="mono">${fmtMin(d.runMs / 60000)}</td><td class="mono">+${sc?.cardBonusMin ?? 0}</td><td class="mono"><b>${fmtMin(fin)}</b></td></tr>`; })()
      : teams.map((t) => { const sc = client.scoresAll[t.id]; const d = runDisplay(client.runsAll[t.id] ?? null, client.config.headStartSec); return { name: t.name, runMs: d.runMs, card: sc?.cardBonusMin ?? 0, pts: sc?.seekingPoints ?? 0, fin: finalMinutes(d.runMs, sc, pct, true) }; })
          .sort((a, b) => b.fin - a.fin)
          .map((r, i) => `<tr><td>${i === 0 ? "🏆 " : ""}${r.name}</td><td class="mono">${fmtMin(r.runMs / 60000)}</td><td class="mono">+${r.card}m · ${r.pts}pt</td><td class="mono"><b>${fmtMin(r.fin)}</b></td></tr>`).join("");

    const rosterSection = group
      ? `<h3>People</h3><ul class="adm-list">${players.map((p) => `<li><span>${p.displayName} <em class="role role-${client.hiderSet.includes(p.id) ? "hider" : "seeker"}">${client.hiderSet.includes(p.id) ? "hider" : "seeker"}</em></span></li>`).join("") || `<li class="muted">No one yet.</li>`}</ul>`
      : `<h3>Teams</h3><ul class="adm-list">${teams.map((t) => `<li><span><b>${t.name}</b> — ${players.filter((p) => p.teamId === t.id).map((p) => p.displayName).join(", ") || "(no one)"}</span></li>`).join("") || `<li class="muted">No teams.</li>`}</ul>`;

    const archiveLabel = (a: import("../net").ArchivedRound) => a.mode === "group" ? (a.hiderSet?.map(pname).join(", ") || "the hiders") : teamName(a.hiderTeamId);

    const modal = document.createElement("div");
    modal.id = "info-modal";
    modal.className = "modal-backdrop";
    modal.innerHTML = `
      <div class="modal">
        <div class="modal-head"><h2>Game info</h2><button class="x" id="info-close">×</button></div>
        <div class="info-hider">Current hider: <b>${curHider}</b></div>
        ${canClaim ? `<button class="primary commit" id="claim-btn">Claim hider (we caught them!)</button>` : ""}
        <h3>Scores</h3>
        <table class="score-table"><thead><tr><th>${group ? "Hiders" : "Team"}</th><th>Run</th><th>${group ? "+Cards" : "Bonus"}</th><th>Final</th></tr></thead><tbody>${scoreRows || `<tr><td class="muted" colspan="4">Nothing yet.</td></tr>`}</tbody></table>
        <p class="muted" style="font-size:11px">Final = (run + card min)${group ? "" : " × (1 + pts × " + pct + "%)"}. Longest wins.</p>
        ${rosterSection}
        <h3>Past rounds</h3>
        <ul class="adm-list" id="archive-list">${client.archives.length ? [...client.archives].reverse().map((a) => { const fin = finalMinutes(runDisplay(a.run, a.headStartSec).runMs, a.score, a.bonusPct ?? pct, a.mode !== "group"); return `<li><span>Hider ${archiveLabel(a)} · final <b>${fmtMin(fin)}</b></span><button class="ghost sm" data-review="${a.id}">Review</button></li>`; }).join("") : `<li class="muted">None yet.</li>`}</ul>
      </div>`;
    document.body.appendChild(modal);
    const close = () => modal.remove();
    modal.addEventListener("click", (e) => { if (e.target === modal) close(); });
    modal.querySelector("#info-close")!.addEventListener("click", close);
    modal.querySelector("#claim-btn")?.addEventListener("click", () => {
      const hr = hideRunKey ? client.runsAll[hideRunKey] : null;
      if (hr && hr.startedAt && !hr.stoppedAt) { alert("The current hider's run must end first."); return; }
      if (confirm("Claim hider? You become the hider, the current hider becomes a seeker, and the board resets for a new round (scores are kept).")) client.claimHider();
      close();
    });
    modal.querySelectorAll<HTMLButtonElement>("[data-review]").forEach((b) => b.addEventListener("click", () => {
      const a = client.archives.find((x) => x.id === b.dataset.review); if (a) openReview(a);
    }));
  }

  // Pod Rotation has no teams/claim-hider; its Game info shows the standings.
  function openPodInfo() {
    const p = client.pod;
    const sched = p?.schedule;
    const nm = (id: string) => esc(p?.names?.[id] || id.slice(0, 5));
    const bonus = p?.bonusMin ?? 20;
    const done = sched ? p!.roundIdx >= sched.pods.length : false;
    const podRows = sched ? sched.pods.map((pod, i) => ({ pod, mins: (p!.podHideMs[`pod${i}`] ?? 0) / 60000 + (client.scoresAll[`pod${i}`]?.cardBonusMin ?? 0) })).sort((a, b) => b.mins - a.mins) : [];
    const seekers = p ? Object.entries(p.catchPoints).map(([id, pts]) => ({ id, pts })).sort((a, b) => b.pts - a.pts) : [];
    const modal = document.createElement("div");
    modal.id = "info-modal";
    modal.className = "modal-backdrop";
    modal.innerHTML = `
      <div class="modal">
        <div class="modal-head"><h2>Game info</h2><button class="x" id="info-close">×</button></div>
        <div class="info-hider">${sched ? (done ? `Game complete · ${sched.pods.length} rounds` : `Round <b>${p!.roundIdx + 1}</b> of ${sched.pods.length}`) : "Waiting for the gamemaster to start…"}</div>
        <h3>🏆 Hider title — longest pod hide</h3>
        <ul class="adm-list">${podRows.map((r, i) => `<li><span>${i === 0 && r.mins > 0 ? "👑 " : ""}${r.pod.map(nm).join(", ")}</span><b>${fmtMin(r.mins)}</b></li>`).join("") || `<li class="muted">No rounds yet.</li>`}</ul>
        <h3>🥷 Seeker title — catch points</h3>
        <ul class="adm-list">${seekers.length ? seekers.map((r, i) => `<li><span>${i === 0 ? "👑 " : ""}${nm(r.id)}</span><b>${r.pts} pt · ${fmtMin(r.pts * bonus)}</b></li>`).join("") : `<li class="muted">No catches yet.</li>`}</ul>
        <p class="muted" style="font-size:11px">Open the ☰ menu → <b>Game schedule</b> for pods, round order &amp; squads.</p>
      </div>`;
    document.body.appendChild(modal);
    const close = () => modal.remove();
    modal.addEventListener("click", (e) => { if (e.target === modal) close(); });
    modal.querySelector("#info-close")!.addEventListener("click", close);
  }

  function openReview(a: ArchivedRound) {
    const teamIds = Object.keys(a.boards);
    let viewTeam = (client.identity.teamId && a.boards[client.identity.teamId]) ? client.identity.teamId : teamIds[0];
    const modal = document.createElement("div");
    modal.className = "modal-backdrop";
    modal.innerHTML = `
      <div class="modal">
        <div class="modal-head"><h2>Round review — hider ${teamName(a.hiderTeamId)}</h2><button class="x" id="rev-close">×</button></div>
        <div class="row"><label>Team board: <select id="rev-team">${teamIds.map((t) => `<option value="${t}" ${t === viewTeam ? "selected" : ""}>${teamName(t)}</option>`).join("")}</select></label></div>
        <div id="rev-map" class="rev-map"></div>
        <h3>Actions taken</h3>
        <ul class="qa-list" id="rev-log"></ul>
      </div>`;
    document.body.appendChild(modal);
    modal.addEventListener("click", (e) => { if (e.target === modal) modal.remove(); });
    modal.querySelector("#rev-close")!.addEventListener("click", () => modal.remove());
    const sel = modal.querySelector<HTMLSelectElement>("#rev-team")!;
    const mapEl = modal.querySelector<HTMLElement>("#rev-map")!;
    const logEl = modal.querySelector<HTMLUListElement>("#rev-log")!;
    let map: maplibregl.Map | null = null;
    const render = () => {
      const board = a.boards[viewTeam];
      logEl.innerHTML = board?.log.length ? [...board.log].reverse().map((l) => `<li>${l.text}</li>`).join("") : `<li class="muted">No actions.</li>`;
      drawStatic();
    };
    function drawStatic() {
      const board = a.boards[viewTeam];
      const eligible = STATIONS.filter((s) => !board?.stations[s.id] && !isBlocked(s.id, a.offLimits)).map((s) => s.id);
      if (!map) {
        map = new maplibregl.Map({ container: mapEl, style: { version: 8, glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf", sources: { osm: { type: "raster", tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"], tileSize: 256 } }, layers: [{ id: "osm", type: "raster", source: "osm" }] }, center: [-73.9712, 40.78], zoom: 11, attributionControl: false });
        map.on("load", () => { addStatic(map!, eligible, a, board); });
      } else if (map.isStyleLoaded()) {
        (map.getSource("z") as maplibregl.GeoJSONSource).setData(clippedZoneFC(a.offLimits));
        (map.getSource("mk") as maplibregl.GeoJSONSource).setData({ type: "FeatureCollection", features: [buildMask(eligible, a.offLimits)] });
        applyElim(map, board);
      }
    }
    render();
    sel.addEventListener("change", () => { viewTeam = sel.value; render(); });
  }

  function addStatic(map: maplibregl.Map, eligible: string[], a: ArchivedRound, board?: { stations: Record<string, "eliminated"> }) {
    map.addSource("mk", { type: "geojson", data: { type: "FeatureCollection", features: [buildMask(eligible, a.offLimits)] } });
    map.addSource("z", { type: "geojson", data: clippedZoneFC(a.offLimits) });
    map.addSource("ol", { type: "geojson", data: offLimitsFC(a.offLimits) });
    map.addLayer({ id: "mk", type: "fill", source: "mk", paint: { "fill-color": "#222a26", "fill-opacity": 0.5 } });
    map.addLayer({ id: "olf", type: "fill", source: "ol", paint: { "fill-color": "#e67e22", "fill-opacity": 0.22 } });
    map.addLayer({ id: "zf", type: "fill", source: "z", paint: { "fill-color": "#1f9d55", "fill-opacity": ["case", ["boolean", ["feature-state", "elim"], false], 0, 0.18] } });
    map.addLayer({ id: "zl", type: "line", source: "z", paint: { "line-color": "#1f9d55", "line-width": 1, "line-opacity": ["case", ["boolean", ["feature-state", "elim"], false], 0, 1] } });
    applyElim(map, board);
  }
  function applyElim(map: maplibregl.Map, board?: { stations: Record<string, "eliminated"> }) {
    for (const s of STATIONS) map.setFeatureState({ source: "z", id: STATION_INDEX[s.id] }, { elim: !!board?.stations[s.id] });
  }

  // Refresh the floating data each time live state changes.
  client.subscribe(() => {});
}
